// Meta (Facebook) Pixel event helper.
//
// The base pixel + PageView is loaded in index.html, guarded so it only
// initializes on the production marketing hosts. That means `window.fbq` is
// simply undefined in dev, on preview deploys, and when an ad blocker strips
// the pixel. This wrapper fails safe in all those cases so a tracking call can
// never break the app or send noise from non-production traffic.
//
// Usage:
//   import { trackPixel } from '@/lib/metaPixel';
//   trackPixel('Lead', { content_name: 'signup' });
//   trackPixel('Purchase', { value: 11.49, currency: 'EUR' }, stripeSessionId);
//   trackPixel('Purchase', { value: 11.49, currency: 'EUR' }, stripeSessionId);
//
// eventID: pass the same id the server sends as event_id on its Conversions
// API event (the Stripe checkout session id for Purchase) so Meta dedupes the
// browser and server copies into one conversion instead of counting two.
export function trackPixel(event, params, eventID) {
  try {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
    window.fbq('track', event, params || {}, eventID ? { eventID: String(eventID) } : undefined);
  } catch {
    /* non-fatal: the pixel must never interrupt the user flow */
  }
}

/**
 * Read Meta's own identifiers out of the cookies its pixel sets:
 *   _fbp - browser id, set on first pixel load
 *   _fbc - click id, set when someone lands with ?fbclid= on the URL
 *
 * `_fbc` is the single strongest attribution signal there is: it names the
 * exact ad click. Handing it to the Conversions API is what lets a conversion
 * be credited to the creative that earned it instead of landing in the
 * unattributed bucket. Returns {} when the pixel never loaded (dev, ad
 * blocker, consent declined), which callers can spread safely.
 */
export function readMetaCookies() {
  try {
    if (typeof document === 'undefined') return {};
    const jar = document.cookie || '';
    const pick = (name) => {
      const m = jar.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
      return m ? decodeURIComponent(m[1]) : undefined;
    };
    const fbp = pick('_fbp');
    const fbc = pick('_fbc');
    return { ...(fbp ? { fbp } : {}), ...(fbc ? { fbc } : {}) };
  } catch {
    return {};
  }
}

/**
 * One id shared by the browser pixel and the server CAPI copy of an event, so
 * Meta collapses them into a single conversion rather than double-counting.
 */
export function newEventId(prefix = 'evt') {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}
